import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { Alert, AuthShell, Button, Field } from '../components/ui';

/**
 * First-run wizard. Only reachable while the instance has no superadmin; the
 * server enforces that independently, this is just the UI half.
 */
export default function SetupPage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    bootstrapKey: '',
    displayName: '',
    email: '',
    password: '',
    confirm: '',
  });

  const update = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (form.password !== form.confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (form.password.length < 12) {
      setError('Use at least 12 characters — length matters more than symbols.');
      return;
    }

    setBusy(true);
    try {
      await api.bootstrap({
        bootstrapKey: form.bootstrapKey.trim(),
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        password: form.password,
      });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Setup failed. Check the server logs and retry.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Claim this instance"
      subtitle="No administrator exists yet. The bootstrap key from the server environment unlocks this one-time step."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error ? <Alert>{error}</Alert> : null}

        <Field
          label="Bootstrap key"
          type="password"
          autoComplete="off"
          required
          value={form.bootstrapKey}
          onChange={update('bootstrapKey')}
          hint="Value of BOOTSTRAP_KEY in the deployment environment."
        />
        <Field
          label="Your name"
          autoComplete="name"
          required
          value={form.displayName}
          onChange={update('displayName')}
        />
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={form.email}
          onChange={update('email')}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={update('password')}
          hint="At least 12 characters. A passphrase beats a short cryptic string."
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          required
          value={form.confirm}
          onChange={update('confirm')}
        />

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Create superadmin
        </Button>

        <p className="text-xs text-ink-2">
          After this succeeds, rotate <code className="text-ink-2">BOOTSTRAP_KEY</code> — the
          endpoint disables itself, but there is no reason to keep a stale secret around.
        </p>
      </form>
    </AuthShell>
  );
}
